import { GOAL_TYPE_NONE, getLocalizedValue, parseMultiselectValue, pickT } from "@repo/shared";
import { useEffect, useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useGoalTypes } from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { T } from "./goal-type-condition-picker.i18n";

type GoalTypeConditionPickerProps = {
  portfolioCollectionId: string;
  /** JSON array of goal-type ids (the rule's stored `value`). */
  value: string;
  onChange: (value: string) => void;
  /** Flow language (flow editor) or template language (PDF editor). */
  language: string;
  disabled?: boolean;
};

export function GoalTypeConditionPicker({
  portfolioCollectionId,
  value,
  onChange,
  language,
  disabled = false,
}: GoalTypeConditionPickerProps) {
  const t = pickT(T, language);
  const { tenantDefaultLanguage } = useUserLanguage();
  const { goalTypes, isLoading } = useGoalTypes(portfolioCollectionId);

  const options = useMemo(
    () => [
      ...goalTypes.map((gt) => ({
        id: gt.id,
        label: getLocalizedValue(gt.labelI18n, language, tenantDefaultLanguage) || gt.id,
      })),
      { id: GOAL_TYPE_NONE, label: t.noType },
    ],
    [goalTypes, language, tenantDefaultLanguage, t.noType],
  );

  const selected = useMemo(() => parseMultiselectValue(value), [value]);
  const knownIds = useMemo(() => options.map((o) => o.id).join("\u0000"), [options]);

  // Drop ids that are no longer in the collection — only once the list is known,
  // so an existing rule's ticks never flash as unrecognised while loading.
  useEffect(() => {
    if (isLoading) return;
    const known = new Set(knownIds.split("\u0000"));
    const kept = selected.filter((id) => known.has(id));
    if (kept.length !== selected.length) onChange(JSON.stringify(kept));
  }, [isLoading, knownIds, selected, onChange]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2" aria-busy="true">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-5 w-36" />
      </div>
    );
  }

  if (goalTypes.length === 0) {
    return <p className="text-xs text-muted-foreground">{t.noGoalTypes}</p>;
  }

  const toggle = (id: string) => {
    const current = new Set(selected);
    if (current.has(id)) current.delete(id);
    else current.add(id);
    // Keep the collection's order so the stored value is stable across toggles.
    const next = options.map((o) => o.id).filter((oid) => current.has(oid));
    onChange(JSON.stringify(next));
  };

  return (
    <div className="flex flex-col gap-2">
      {options.map((opt) => (
        <div key={opt.id} className="flex items-center gap-2 text-sm font-normal">
          <Checkbox
            id={`goal-type-condition-${opt.id}`}
            checked={selected.includes(opt.id)}
            onCheckedChange={() => toggle(opt.id)}
            disabled={disabled}
          />
          <Label htmlFor={`goal-type-condition-${opt.id}`} className="font-normal cursor-pointer">
            {opt.label}
          </Label>
        </div>
      ))}
    </div>
  );
}
